import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { usePermissions, PERMISSIONS } from '../hooks/usePermissions';
import { useUsers } from '../context/UsersContext';
import SearchableDropdown from '../components/SearchableDropdown';
import MonthYearPicker from '../components/MonthYearPicker';
import USDateInput from '../components/USDateInput';
import CustomTooltip from '../components/Tooltip';
import { Info } from 'lucide-react';
import { getCurrentMonthYear, formatMonthYear, getMonthRange, formatDateRange } from '../utils/dateUtils';
import { filterEmojis } from '../utils/emojiFilter';
import './DrlDashboard.css';

const DrlDashboard = () => {
  const { showError } = useNotification();
  const { can } = usePermissions();
  const { users } = useUsers();
  
  // DRL dashboard permission
  const hasDashboardPermission = can(PERMISSIONS.DIT_DRL_DASHBOARD_VIEW);
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [practices, setPractices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    drlReceivedFrom: '',
    drlReceivedTo: '',
    createdDateFrom: '',
    createdDateTo: '',
    reconciliationDateFrom: '',
    reconciliationDateTo: '',
    completedDateFrom: '',
    completedDateTo: '',
    practiceCodes: [],
    locationCodes: [],
    statuses: [],
    assigneeIds: [],
    reporteeIds: [],
    reconciliationStatuses: [],
    drlNumber: ''
  });
  
  // Refs for "To" date inputs to auto-open calendar
  const drlReceivedToRef = useRef(null);
  const createdDateToRef = useRef(null);
  const reconciliationDateToRef = useRef(null);
  const completedDateToRef = useRef(null);

  // Month/Year selection
  const currentMonthYear = getCurrentMonthYear();
  const [selectedMonth, setSelectedMonth] = useState(currentMonthYear.month);
  const [selectedYear, setSelectedYear] = useState(currentMonthYear.year);
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const handleMonthYearSelect = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setShowMonthPicker(false);
  };

  // Ref to prevent duplicate API calls
  const practicesFetchedRef = useRef(false);
  
  const fetchPractices = async () => {
    // Only fetch practices if user has dashboard permission
    if (!hasDashboardPermission) {
      return;
    }
    
    // Prevent duplicate calls
    if (practicesFetchedRef.current) {
      return;
    }
    
    practicesFetchedRef.current = true;
    
    try {
      const response = await api.getAllPractices();
      const practicesList = Array.isArray(response) ? response : (response?.items || []);
      setPractices(practicesList.filter(p => p.isActive !== false));
    } catch (error) {
      console.error('Error fetching practices:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch practices';
      showError(errorMessage);
      practicesFetchedRef.current = false; // Reset on error to allow retry
    }
  };

  const fetchKPIData = useCallback(async () => {
    // Only fetch KPI data if user has dashboard permission
    if (!hasDashboardPermission) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Build request body - use filters if provided, otherwise use month range
      const requestBody = { ...filters };
      
      // DRL Number (supports wildcards)
      if (requestBody.drlNumber && requestBody.drlNumber.trim() !== '') {
        requestBody.drlNumber = requestBody.drlNumber.trim();
      }
      
      // Check if user has set any date filters
      const hasDrlReceivedDateFilter = filters.drlReceivedFrom || filters.drlReceivedTo;
      const hasCreatedDateFilter = filters.createdDateFrom || filters.createdDateTo;
      const hasReconciliationDateFilter = filters.reconciliationDateFrom || filters.reconciliationDateTo;
      const hasCompletedDateFilter = filters.completedDateFrom || filters.completedDateTo;
      const hasOtherFilters = (
        (filters.practiceCodes && filters.practiceCodes.length > 0) ||
        (filters.locationCodes && filters.locationCodes.length > 0) ||
        (filters.statuses && filters.statuses.length > 0) ||
        (filters.assigneeIds && filters.assigneeIds.length > 0) ||
        (filters.reporteeIds && filters.reporteeIds.length > 0) ||
        (filters.reconciliationStatuses && filters.reconciliationStatuses.length > 0) ||
        (filters.drlNumber && filters.drlNumber.trim() !== '')
      );
      
      // Only add default DRL received date if:
      // 1. No date filters are set at all
      // 2. AND no other filters are set
      if (!hasDrlReceivedDateFilter && !hasCreatedDateFilter && !hasReconciliationDateFilter && !hasCompletedDateFilter && !hasOtherFilters) {
        const monthRange = getMonthRange(selectedMonth, selectedYear);
        requestBody.drlReceivedFrom = monthRange.startDate;
        requestBody.drlReceivedTo = monthRange.endDate;
      } else {
        // If user selected other date ranges, remove DRL received date if not explicitly set
        if (!hasDrlReceivedDateFilter) {
          delete requestBody.drlReceivedFrom;
          delete requestBody.drlReceivedTo;
        }
      }
      
      // Clean up empty values - only include params that user explicitly set
      if (!requestBody.drlReceivedFrom) delete requestBody.drlReceivedFrom;
      if (!requestBody.drlReceivedTo) delete requestBody.drlReceivedTo;
      if (!requestBody.createdDateFrom) delete requestBody.createdDateFrom;
      if (!requestBody.createdDateTo) delete requestBody.createdDateTo;
      if (!requestBody.reconciliationDateFrom) delete requestBody.reconciliationDateFrom;
      if (!requestBody.reconciliationDateTo) delete requestBody.reconciliationDateTo;
      if (!requestBody.completedDateFrom) delete requestBody.completedDateFrom;
      if (!requestBody.completedDateTo) delete requestBody.completedDateTo;
      if (!requestBody.practiceCodes || requestBody.practiceCodes.length === 0) delete requestBody.practiceCodes;
      if (!requestBody.locationCodes || requestBody.locationCodes.length === 0) delete requestBody.locationCodes;
      if (!requestBody.statuses || requestBody.statuses.length === 0) delete requestBody.statuses;
      if (!requestBody.assigneeIds || requestBody.assigneeIds.length === 0) delete requestBody.assigneeIds;
      if (!requestBody.reporteeIds || requestBody.reporteeIds.length === 0) delete requestBody.reporteeIds;
      if (!requestBody.reconciliationStatuses || requestBody.reconciliationStatuses.length === 0) delete requestBody.reconciliationStatuses;
      if (!requestBody.drlNumber || requestBody.drlNumber.trim() === '') delete requestBody.drlNumber;
      
      const response = await api.getDrlDashboardKPIs(requestBody);
      setKpiData(response);
    } catch (err) {
      console.error('Error fetching DRL KPI data:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to load DRL dashboard data';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedMonth, selectedYear, showError, hasDashboardPermission]);

  useEffect(() => {
    // Only fetch data if user has permission
    if (hasDashboardPermission) {
      fetchPractices();
      fetchKPIData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKPIData, hasDashboardPermission]);

  useEffect(() => {
    fetchKPIData();
  }, [fetchKPIData]);

  // Sync local filters with applied filters when sidebar opens
  useEffect(() => {
    if (showFilterSidebar) {
      setLocalFilters({
        drlReceivedFrom: filters.drlReceivedFrom || '',
        drlReceivedTo: filters.drlReceivedTo || '',
        createdDateFrom: filters.createdDateFrom || '',
        createdDateTo: filters.createdDateTo || '',
        reconciliationDateFrom: filters.reconciliationDateFrom || '',
        reconciliationDateTo: filters.reconciliationDateTo || '',
        completedDateFrom: filters.completedDateFrom || '',
        completedDateTo: filters.completedDateTo || '',
        practiceCodes: filters.practiceCodes || [],
        locationCodes: filters.locationCodes || [],
        statuses: filters.statuses || [],
        assigneeIds: filters.assigneeIds || [],
        reporteeIds: filters.reporteeIds || [],
        reconciliationStatuses: filters.reconciliationStatuses || [],
        drlNumber: filters.drlNumber || ''
      });
    }
  }, [showFilterSidebar, filters]);

  // Fetch locations when practices are selected
  useEffect(() => {
    if (localFilters.practiceCodes && localFilters.practiceCodes.length > 0 && practices.length > 0) {
      const fetchAllLocations = async () => {
        const allLocations = [];
        for (const practiceCode of localFilters.practiceCodes) {
          try {
            const practice = practices.find(p => p.code === practiceCode);
            if (practice && practice.practiceId) {
              const response = await api.getPracticeLocations(practice.practiceId);
              const locationsList = Array.isArray(response) ? response : (response?.items || []);
              allLocations.push(...locationsList);
            }
          } catch (error) {
            console.error(`Error fetching locations for practice ${practiceCode}:`, error);
            const errorMessage = error?.message || error?.data?.message || `Failed to fetch locations for practice ${practiceCode}`;
            showError(errorMessage);
          }
        }
        setLocations(allLocations);
      };
      fetchAllLocations();
    } else {
      setLocations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters.practiceCodes, practices]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getSubtitle = () => {
    if (!kpiData?.scope) {
      // If no data, check if we have filters applied to show relevant subtitle
      if (hasActiveFilters()) {
        return 'Overview of DRL reconciliation';
      }
      return 'Overview of DRL reconciliation for Current Month';
    }
    const { 
      drlReceivedFrom, 
      drlReceivedTo, 
      createdDateFrom, 
      createdDateTo 
    } = kpiData.scope;
    
    // Check which date range is being used (priority: drlReceived > created)
    if (drlReceivedFrom && drlReceivedTo) {
      return `Overview of DRL reconciliation for DRL Received Date Range: ${formatDate(drlReceivedFrom)} - ${formatDate(drlReceivedTo)}`;
    }
    if (createdDateFrom && createdDateTo) {
      return `Overview of DRL reconciliation for Created Date Range: ${formatDate(createdDateFrom)} - ${formatDate(createdDateTo)}`;
    }
    // If only one date is set, show it
    if (drlReceivedFrom) {
      return `Overview of DRL reconciliation for DRL Received Date From: ${formatDate(drlReceivedFrom)}`;
    }
    if (createdDateFrom) {
      return `Overview of DRL reconciliation for Created Date From: ${formatDate(createdDateFrom)}`;
    }
    return 'Overview of DRL reconciliation for Current Month';
  };

  const getDateRangeDisplay = () => {
    // If custom filters are active, show "Custom Range"
    if (hasActiveFilters()) {
      return 'Custom Range';
    }
    // Show month/year format
    return formatMonthYear(selectedMonth, selectedYear);
  };

  const handleFilterChange = (field, value) => {
    // Filter emojis from string inputs
    const filteredValue = typeof value === 'string' ? filterEmojis(value) : value;
    
    // Check if this is an end date field being set without a start date
    const endDateFields = {
      'drlReceivedTo': 'drlReceivedFrom',
      'createdDateTo': 'createdDateFrom',
      'reconciliationDateTo': 'reconciliationDateFrom',
      'completedDateTo': 'completedDateFrom'
    };
    
    // Reverse mapping: start date fields to end date fields (for clearing end date when start date is cleared)
    const startDateFields = {
      'drlReceivedFrom': 'drlReceivedTo',
      'createdDateFrom': 'createdDateTo',
      'reconciliationDateFrom': 'reconciliationDateTo',
      'completedDateFrom': 'completedDateTo'
    };
    
    const stringValue = typeof filteredValue === 'string' ? filteredValue : String(filteredValue || '');
    
    // If user is clearing a start date, also clear the corresponding end date
    if (startDateFields[field] && (!stringValue || stringValue.trim() === '')) {
      const endDateField = startDateFields[field];
      setLocalFilters(prev => ({
        ...prev,
        [field]: '',
        [endDateField]: '' // Clear the corresponding end date
      }));
      return; // Exit early after clearing both dates
    }
    
    // If user is trying to set an end date without a start date, show error and prevent
    if (endDateFields[field] && stringValue && stringValue.trim() !== '') {
      const startDateField = endDateFields[field];
      const startDateValue = localFilters[startDateField];
      
      if (!startDateValue || startDateValue.trim() === '') {
        const fieldLabels = {
          'drlReceivedTo': 'DRL Received Date',
          'createdDateTo': 'Created Date',
          'reconciliationDateTo': 'Reconciliation Date',
          'completedDateTo': 'Completed Date'
        };
        showError(`First you need to select start date for ${fieldLabels[field] || 'the date range'}.`);
        return; // Don't set the value
      }
    }
    
    setLocalFilters(prev => ({
      ...prev,
      [field]: filteredValue
    }));
    
    // Auto-open corresponding "To" date calendar when "From" date is selected
    if (stringValue && stringValue.trim() !== '') {
      const datePairMap = {
        'drlReceivedFrom': drlReceivedToRef,
        'createdDateFrom': createdDateToRef,
        'reconciliationDateFrom': reconciliationDateToRef,
        'completedDateFrom': completedDateToRef
      };
      
      const toDateRef = datePairMap[field];
      if (toDateRef && toDateRef.current) {
        // Use a small delay to ensure the date value is processed first
        setTimeout(() => {
          if (toDateRef.current && toDateRef.current.openCalendar) {
            toDateRef.current.openCalendar();
          }
        }, 300);
      }
    }
  };

  const [selectedPracticeValue, setSelectedPracticeValue] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const exportToPDF = async () => {
    if (isExporting) return;
    
    try {
      setIsExporting(true);
      
      // Create a temporary container for PDF content
      const printContainer = document.createElement('div');
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '0';
      printContainer.style.width = '1200px';
      printContainer.style.background = 'white';
      printContainer.style.padding = '40px';
      document.body.appendChild(printContainer);

      // Build PDF content
      let htmlContent = `
        <div style="font-family: Arial, sans-serif; color: #111827;">
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #3b82f6; padding-bottom: 20px;">
            <h1 style="margin: 0 0 10px 0; font-size: 32px; color: #111827;">DRL Dashboard</h1>
            <p style="margin: 0; font-size: 16px; color: #6b7280;">${getSubtitle()}</p>
            <p style="margin: 10px 0 0 0; font-size: 14px; color: #9ca3af;">Generated on ${new Date().toLocaleString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit'
            })}</p>
          </div>
      `;

      // Add active filters if any
      if (hasActiveFilters()) {
        const appliedFilters = getAppliedFilters();
        htmlContent += `
          <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin-bottom: 30px;">
            <h3 style="margin: 0 0 15px 0; font-size: 18px; color: #374151;">Active Filters</h3>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
        `;
        
        // Date Filters
        if (appliedFilters.drlReceivedFrom || appliedFilters.drlReceivedTo) {
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">DRL Received Date:</strong> ${formatDateRange(appliedFilters.drlReceivedFrom || '', appliedFilters.drlReceivedTo || '')}
            </div>
          `;
        }
        
        if (appliedFilters.createdDateFrom || appliedFilters.createdDateTo) {
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">Created Date:</strong> ${formatDateRange(appliedFilters.createdDateFrom || '', appliedFilters.createdDateTo || '')}
            </div>
          `;
        }
        
        if (appliedFilters.reconciliationDateFrom || appliedFilters.reconciliationDateTo) {
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">Reconciliation Date:</strong> ${formatDateRange(appliedFilters.reconciliationDateFrom || '', appliedFilters.reconciliationDateTo || '')}
            </div>
          `;
        }
        
        if (appliedFilters.completedDateFrom || appliedFilters.completedDateTo) {
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">Completed Date:</strong> ${formatDateRange(appliedFilters.completedDateFrom || '', appliedFilters.completedDateTo || '')}
            </div>
          `;
        }
        
        // Practices
        if (appliedFilters.practiceCodes && appliedFilters.practiceCodes.length > 0) {
          const practiceNames = appliedFilters.practiceCodes.map(code => 
            practices.find(p => p.code === code)?.name || code
          ).join(', ');
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">Practices:</strong> ${practiceNames}
            </div>
          `;
        }
        
        // Locations
        if (appliedFilters.locationCodes && appliedFilters.locationCodes.length > 0) {
          const locationNames = appliedFilters.locationCodes.map(code => 
            locations.find(l => l.code === code)?.name || code
          ).join(', ');
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">Locations:</strong> ${locationNames}
            </div>
          `;
        }
        
        // Statuses
        if (appliedFilters.statuses && appliedFilters.statuses.length > 0) {
          const statusLabels = {
            'NOT_STARTED': 'Not Started',
            'IN_PROGRESS': 'In Progress',
            'UNDER_CLARIFICATIONS': 'Under Clarifications',
            'COMPLETED': 'Completed',
            'OVER_POSTED': 'Over Posted'
          };
          const statusNames = appliedFilters.statuses.map(s => statusLabels[s] || s).join(', ');
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">Statuses:</strong> ${statusNames}
            </div>
          `;
        }
        
        // Assignees
        if (appliedFilters.assigneeIds && appliedFilters.assigneeIds.length > 0) {
          const assigneeNames = appliedFilters.assigneeIds.map(id => {
            const user = users.find(u => (u.userId || u.id) === id);
            return user && user.firstName && user.lastName 
              ? `${user.firstName} ${user.lastName}` 
              : user?.email || id;
          }).join(', ');
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">Assignees:</strong> ${assigneeNames}
            </div>
          `;
        }
        
        // Reporters
        if (appliedFilters.reporteeIds && appliedFilters.reporteeIds.length > 0) {
          const reporterNames = appliedFilters.reporteeIds.map(id => {
            const user = users.find(u => (u.userId || u.id) === id);
            return user && user.firstName && user.lastName 
              ? `${user.firstName} ${user.lastName}` 
              : user?.email || id;
          }).join(', ');
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">Reporters:</strong> ${reporterNames}
            </div>
          `;
        }
        
        // Reconciliation Statuses
        if (appliedFilters.reconciliationStatuses && appliedFilters.reconciliationStatuses.length > 0) {
          const reconStatusLabels = {
            'RECONCILED': 'Reconciled',
            'OVER_RECONCILED': 'Excess Reconciled',
            'UNRECONCILED': 'Pending Reconciliation'
          };
          const reconStatusNames = appliedFilters.reconciliationStatuses.map(s => reconStatusLabels[s] || s).join(', ');
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">Reconciliation Status:</strong> ${reconStatusNames}
            </div>
          `;
        }
        
        // DRL Number
        if (appliedFilters.drlNumber) {
          htmlContent += `
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;">
              <strong style="color: #374151;">DRL Number:</strong> ${appliedFilters.drlNumber}
            </div>
          `;
        }
        
        htmlContent += `
            </div>
          </div>
        `;
      }

      // Add KPI Cards if available
      if (hasAmountData && amountSummary) {
        htmlContent += `
          <div style="margin-bottom: 30px;">
            <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827;">Payment Type Breakdown</h3>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
              <div style="background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); border-left: 4px solid #10b981; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">eCheck Sum</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.eCheckAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${((amountSummary.eCheckAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); border-left: 4px solid #f59e0b; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Paper Check</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.paperCheckAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${((amountSummary.paperCheckAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%); border-left: 4px solid #8b5cf6; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Total Credit Card</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.creditCardAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${((amountSummary.creditCardAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border-left: 4px solid #3b82f6; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">= Total DRL Amount</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.totalAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${totalCount || 0} DRLs</div>
              </div>
            </div>
          </div>

          <div style="margin-bottom: 30px;">
            <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827;">Posting Breakdown</h3>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
              <div style="background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%); border-left: 4px solid #06b6d4; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Onshore Posting</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.onshorePostedAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${((amountSummary.onshorePostedAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%); border-left: 4px solid #0284c7; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Offshore Posting</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.offshorePostedAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${((amountSummary.offshorePostedAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%); border-left: 4px solid #6b7280; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Other Amount</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.otherAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${((amountSummary.otherAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border-left: 4px solid #3b82f6; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">= Total Posting</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(
                  (amountSummary.onshorePostedAmount || 0) + 
                  (amountSummary.offshorePostedAmount || 0) + 
                  (amountSummary.otherAmount || 0)
                )}</div>
                <div style="font-size: 13px; color: #6b7280;">Total posted amounts</div>
              </div>
            </div>
          </div>

          <div style="margin-bottom: 30px;">
            <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827;">Bank and Posting Status</h3>
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px;">
              <div style="background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%); border-left: 4px solid #8b5cf6; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Total Received in Bank</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(
                  (amountSummary.totalAmount || 0) - (amountSummary.remainingBankAmount || 0)
                )}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${(((amountSummary.totalAmount - amountSummary.remainingBankAmount) / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0% of total'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%); border-left: 4px solid #6366f1; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Total Remaining in Bank</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.remainingBankAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${((amountSummary.remainingBankAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); border-left: 4px solid #f59e0b; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Total Remaining in Posting</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.remainingAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${((amountSummary.remainingAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
            </div>
          </div>
        `;
      }

      // Add Reconciliation Breakdown if available
      if (kpiData?.single?.reconciliationBreakdown && kpiData.single.reconciliationBreakdown.length > 0) {
        htmlContent += `
          <div style="margin-bottom: 30px;">
            <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827;">Reconciliation Status</h3>
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px;">
              <div style="background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); border-left: 4px solid #dc2626; padding: 20px; border-radius: 8px; text-align: center;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Unreconciled</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${kpiData.single.unreconciledCount || 0}</div>
                <div style="font-size: 13px; color: #6b7280;">${totalCount ? `${(((kpiData.single.unreconciledCount || 0) / totalCount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); border-left: 4px solid #10b981; padding: 20px; border-radius: 8px; text-align: center;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Reconciled</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${kpiData.single.reconciledCount || 0}</div>
                <div style="font-size: 13px; color: #6b7280;">${totalCount ? `${(((kpiData.single.reconciledCount || 0) / totalCount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); border-left: 4px solid #f59e0b; padding: 20px; border-radius: 8px; text-align: center;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Over Reconciled</div>
                <div style="font-size: 28px; font-weight: bold; color: #111827; margin-bottom: 5px;">${kpiData.single.overReconciledCount || 0}</div>
                <div style="font-size: 13px; color: #6b7280;">${totalCount ? `${(((kpiData.single.overReconciledCount || 0) / totalCount) * 100).toFixed(1)}% of total` : '0%'}</div>
              </div>
            </div>
          </div>
        `;
      }

      // Add Status Breakdown if available
      if (statusBreakdown && statusBreakdown.length > 0) {
        htmlContent += `
          <div style="margin-bottom: 30px;">
            <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827;">Status Breakdown</h3>
            <div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px;">
        `;
        
        statusBreakdown.forEach(status => {
          htmlContent += `
            <div style="background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 15px; text-align: center;">
              <div style="display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 10px;">
                <div style="width: 10px; height: 10px; border-radius: 50%; background: ${STATUS_COLORS[status.status]};"></div>
                <div style="font-size: 13px; color: #374151; font-weight: 500;">${STATUS_LABELS[status.status] || status.status}</div>
              </div>
              <div style="font-size: 24px; font-weight: bold; color: #111827; margin-bottom: 5px;">${status.count}</div>
              <div style="font-size: 14px; color: #6b7280; font-weight: 600;">${status.percentage.toFixed(1)}%</div>
            </div>
          `;
        });
        
        htmlContent += `
            </div>
          </div>
        `;
      }

      htmlContent += `
        </div>
      `;

      printContainer.innerHTML = htmlContent;

      // Wait for content to render
      await new Promise(resolve => setTimeout(resolve, 500));

      // Now capture the charts from the actual page and add them to the PDF
      const chartsSection = document.querySelector('.charts-grid');
      let chartsCanvas = null;
      
      if (chartsSection && statusBreakdown && statusBreakdown.length > 0) {
        try {
          chartsCanvas = await html2canvas(chartsSection, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
          });
        } catch (error) {
          console.error('Error capturing charts:', error);
        }
      }

      // Capture main content as canvas
      const canvas = await html2canvas(printContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Remove temporary container
      document.body.removeChild(printContainer);

      // Create PDF with automatic multi-page support
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate dimensions
      const ratio = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      
      // If content fits on one page
      if (scaledHeight <= pdfHeight) {
        const imgX = (pdfWidth - imgWidth * ratio) / 2;
        pdf.addImage(imgData, 'PNG', imgX, 0, imgWidth * ratio, scaledHeight);
      } else {
        // Content needs multiple pages
        let heightLeft = scaledHeight;
        let position = 0;
        
        // Add first page
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, scaledHeight);
        heightLeft -= pdfHeight;
        
        // Add remaining pages
        while (heightLeft > 0) {
          position = heightLeft - scaledHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, scaledHeight);
          heightLeft -= pdfHeight;
        }
      }

      // Add charts on a new page if available
      if (chartsCanvas) {
        pdf.addPage();
        
        // Add header for charts page
        pdf.setFontSize(20);
        pdf.setTextColor(17, 24, 39);
        pdf.text('Charts & Analytics', pdfWidth / 2, 20, { align: 'center' });
        
        const chartsImgData = chartsCanvas.toDataURL('image/png');
        const chartsWidth = chartsCanvas.width;
        const chartsHeight = chartsCanvas.height;
        const chartsRatio = Math.min((pdfWidth - 20) / chartsWidth, (pdfHeight - 40) / chartsHeight);
        const chartsX = (pdfWidth - chartsWidth * chartsRatio) / 2;
        
        pdf.addImage(chartsImgData, 'PNG', chartsX, 30, chartsWidth * chartsRatio, chartsHeight * chartsRatio);
      }

      // Save PDF
      const fileName = `DRL_Dashboard_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddToArray = (field, value) => {
    if (!value || value === '') return;
    setLocalFilters(prev => {
      const currentArray = prev[field] || [];
      if (currentArray.includes(value)) {
        return prev;
      }
      return {
        ...prev,
        [field]: [...currentArray, value]
      };
    });
  };

  const handleRemoveFromArray = (field, value) => {
    setLocalFilters(prev => {
      const currentArray = prev[field] || [];
      return {
        ...prev,
        [field]: currentArray.filter(item => item !== value)
      };
    });

    // Clear locations if removing a practice
    if (field === 'practiceCodes') {
      setLocalFilters(prev => ({
        ...prev,
        locationCodes: []
      }));
    }
  };

  const handlePracticeAdd = (practiceCode) => {
    if (!practiceCode || practiceCode === '') return;
    handleAddToArray('practiceCodes', practiceCode);
    setSelectedPracticeValue('');
  };

  const handlePracticeRemove = (practiceCode) => {
    handleRemoveFromArray('practiceCodes', practiceCode);
  };

  const applyFilters = () => {
    // Validate date ranges: if a "From" date is selected, the corresponding "To" date must also be selected
    const dateRangePairs = [
      { from: 'drlReceivedFrom', to: 'drlReceivedTo', label: 'DRL Received Date' },
      { from: 'createdDateFrom', to: 'createdDateTo', label: 'Created Date' },
      { from: 'reconciliationDateFrom', to: 'reconciliationDateTo', label: 'Reconciliation Date' },
      { from: 'completedDateFrom', to: 'completedDateTo', label: 'Completed Date' }
    ];

    // Check each date range pair
    for (const pair of dateRangePairs) {
      const fromValue = localFilters[pair.from];
      const toValue = localFilters[pair.to];
      
      // If "From" date is set but "To" date is not set, show error
      if (fromValue && fromValue.trim() !== '' && (!toValue || toValue.trim() === '')) {
        showError(`You need to enter end date for ${pair.label} range.`);
        return false; // Don't apply filters if validation fails
      }
    }

    const newFilters = {};
    
    if (localFilters.drlReceivedFrom) {
      newFilters.drlReceivedFrom = localFilters.drlReceivedFrom;
    }
    if (localFilters.drlReceivedTo) {
      newFilters.drlReceivedTo = localFilters.drlReceivedTo;
    }
    if (localFilters.createdDateFrom) {
      newFilters.createdDateFrom = localFilters.createdDateFrom;
    }
    if (localFilters.createdDateTo) {
      newFilters.createdDateTo = localFilters.createdDateTo;
    }
    if (localFilters.reconciliationDateFrom) {
      newFilters.reconciliationDateFrom = localFilters.reconciliationDateFrom;
    }
    if (localFilters.reconciliationDateTo) {
      newFilters.reconciliationDateTo = localFilters.reconciliationDateTo;
    }
    if (localFilters.completedDateFrom) {
      newFilters.completedDateFrom = localFilters.completedDateFrom;
    }
    if (localFilters.completedDateTo) {
      newFilters.completedDateTo = localFilters.completedDateTo;
    }
    if (localFilters.practiceCodes && localFilters.practiceCodes.length > 0) {
      newFilters.practiceCodes = [...localFilters.practiceCodes];
    }
    if (localFilters.locationCodes && localFilters.locationCodes.length > 0) {
      newFilters.locationCodes = [...localFilters.locationCodes];
    }
    if (localFilters.statuses && localFilters.statuses.length > 0) {
      newFilters.statuses = [...localFilters.statuses];
    }
    if (localFilters.assigneeIds && localFilters.assigneeIds.length > 0) {
      newFilters.assigneeIds = [...localFilters.assigneeIds];
    }
    if (localFilters.reporteeIds && localFilters.reporteeIds.length > 0) {
      newFilters.reporteeIds = [...localFilters.reporteeIds];
    }
    if (localFilters.reconciliationStatuses && localFilters.reconciliationStatuses.length > 0) {
      newFilters.reconciliationStatuses = [...localFilters.reconciliationStatuses];
    }
    if (localFilters.drlNumber && localFilters.drlNumber.trim() !== '') {
      newFilters.drlNumber = localFilters.drlNumber.trim();
    }
    
    setFilters(newFilters);
    return true; // Return true on success
  };

  const clearFilters = () => {
    setLocalFilters({
      drlReceivedFrom: '',
      drlReceivedTo: '',
      createdDateFrom: '',
      createdDateTo: '',
      reconciliationDateFrom: '',
      reconciliationDateTo: '',
      completedDateFrom: '',
      completedDateTo: '',
      practiceCodes: [],
      locationCodes: [],
      statuses: [],
      assigneeIds: [],
      reporteeIds: [],
      reconciliationStatuses: [],
      drlNumber: ''
    });
    setFilters({});
  };

  const hasActiveFilters = () => {
    return (
      filters.drlReceivedFrom ||
      filters.drlReceivedTo ||
      filters.createdDateFrom ||
      filters.createdDateTo ||
      filters.reconciliationDateFrom ||
      filters.reconciliationDateTo ||
      filters.completedDateFrom ||
      filters.completedDateTo ||
      (filters.practiceCodes && filters.practiceCodes.length > 0) ||
      (filters.locationCodes && filters.locationCodes.length > 0) ||
      (filters.statuses && filters.statuses.length > 0) ||
      (filters.assigneeIds && filters.assigneeIds.length > 0) ||
      (filters.reporteeIds && filters.reporteeIds.length > 0) ||
      (filters.reconciliationStatuses && filters.reconciliationStatuses.length > 0) ||
      (filters.drlNumber && filters.drlNumber.trim() !== '')
    );
  };

  const getAppliedFilters = () => {
    const applied = {};
    if (filters.drlReceivedFrom) applied.drlReceivedFrom = filters.drlReceivedFrom;
    if (filters.drlReceivedTo) applied.drlReceivedTo = filters.drlReceivedTo;
    if (filters.createdDateFrom) applied.createdDateFrom = filters.createdDateFrom;
    if (filters.createdDateTo) applied.createdDateTo = filters.createdDateTo;
    if (filters.reconciliationDateFrom) applied.reconciliationDateFrom = filters.reconciliationDateFrom;
    if (filters.reconciliationDateTo) applied.reconciliationDateTo = filters.reconciliationDateTo;
    if (filters.completedDateFrom) applied.completedDateFrom = filters.completedDateFrom;
    if (filters.completedDateTo) applied.completedDateTo = filters.completedDateTo;
    if (filters.practiceCodes) applied.practiceCodes = filters.practiceCodes;
    if (filters.locationCodes) applied.locationCodes = filters.locationCodes;
    if (filters.statuses) applied.statuses = filters.statuses;
    if (filters.assigneeIds) applied.assigneeIds = filters.assigneeIds;
    if (filters.reporteeIds) applied.reporteeIds = filters.reporteeIds;
    if (filters.reconciliationStatuses) applied.reconciliationStatuses = filters.reconciliationStatuses;
    if (filters.drlNumber && filters.drlNumber.trim() !== '') applied.drlNumber = filters.drlNumber;
    return applied;
  };

  const STATUS_COLORS = {
    'NOT_STARTED': '#6b7280',
    'IN_PROGRESS': '#3b82f6',
    'COMPLETED': '#10b981',
    'OVER_POSTED': '#ef4444',
    'UNDER_CLARIFICATIONS': '#f59e0b'
  };

  const STATUS_LABELS = {
    'NOT_STARTED': 'Not Started',
    'IN_PROGRESS': 'In Progress',
    'COMPLETED': 'Completed',
    'OVER_POSTED': 'Over Posted',
    'UNDER_CLARIFICATIONS': 'Clarifications'
  };

  const prepareStatusChartData = () => {
    if (!kpiData?.single?.statusBreakdown) return [];
    return kpiData.single.statusBreakdown.map(item => ({
      name: STATUS_LABELS[item.status] || item.status,
      value: item.count,
      percentage: item.percentage,
      status: item.status
    }));
  };

  const prepareReconciliationStatusChartData = () => {
    if (!kpiData?.single) return [];
    
    const reconciledCount = kpiData.single.reconciledCount || 0;
    const unreconciledCount = kpiData.single.unreconciledCount || 0;
    const overReconciledCount = kpiData.single.overReconciledCount || 0;
    const total = reconciledCount + unreconciledCount + overReconciledCount;
    
    return [
      {
        name: 'Reconciled',
        count: reconciledCount,
        percentage: total > 0 ? (reconciledCount / total) * 100 : 0,
        color: '#10b981'
      },
      {
        name: 'Unreconciled',
        count: unreconciledCount,
        percentage: total > 0 ? (unreconciledCount / total) * 100 : 0,
        color: '#ef4444'
      },
      {
        name: 'Over Reconciled',
        count: overReconciledCount,
        percentage: total > 0 ? (overReconciledCount / total) * 100 : 0,
        color: '#f59e0b'
      }
    ];
  };

  // Check dashboard permission - using CHECK_DASHBOARD_VIEW as the main dashboard permission
  if (!hasDashboardPermission) {
    return (
      <div className="dashboard">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 100px)',
          textAlign: 'center',
          padding: '40px 20px',
          background: '#f9fafb'
        }}>
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '48px 32px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            maxWidth: '500px',
            width: '100%'
          }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '24px'
            }}>
              🔒
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              margin: '0 0 12px 0'
            }}>
              Access Denied
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              You don't have permission to view the Dashboard.
            </p>
            <p style={{
              fontSize: '14px',
              color: '#9ca3af',
              margin: '0',
              lineHeight: '1.5'
            }}>
              Please contact your administrator to request access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Always render dashboard, even when API fails
  // Extract data safely - may be null if API failed
  const { single } = kpiData || {};
  const { totalCount, statusBreakdown, amountSummary } = single || {};
  
  // Check if we have amount data available (hide amount cards if API failed)
  const hasAmountData = kpiData && amountSummary;

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1 className="page-title">DRL Dashboard</h1>
          <p className="page-subtitle">
            {getSubtitle()}
          </p>
        </div>
        <div className="header-actions">
          {!hasActiveFilters() && (
            <div className="date-range-container">
              <button 
                className="date-range-btn"
                onClick={() => setShowMonthPicker(!showMonthPicker)}
              >
                {getDateRangeDisplay()}
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ marginLeft: '8px' }}>
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showMonthPicker && (
                <MonthYearPicker
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onSelect={handleMonthYearSelect}
                  onClose={() => setShowMonthPicker(false)}
                />
              )}
            </div>
          )}
          {hasActiveFilters() && (
            <div className="date-range-container">
              <div className="date-range-display">
                {getDateRangeDisplay()}
              </div>
            </div>
          )}
          <button 
            onClick={exportToPDF}
            disabled={isExporting}
            className="custom-filter-button export-pdf-button"
            title="Export dashboard to PDF"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
            {isExporting ? 'Generating...' : 'Export PDF'}
          </button>
          <button 
            onClick={() => setShowFilterSidebar(true)} 
            className={`custom-filter-button ${hasActiveFilters() ? 'has-filters' : ''}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            Custom Filter
            {hasActiveFilters() && (
              <span className="filter-badge">
                {Object.keys(getAppliedFilters()).length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Active Filters */}
      {hasActiveFilters() && (() => {
        const appliedFilters = getAppliedFilters();
        return (
          <div className="active-filters-section">
            <div className="active-filters-label">Active Filters:</div>
            <div className="active-filters-chips">
              {(appliedFilters.drlReceivedFrom || appliedFilters.drlReceivedTo) && (
                <div className="filter-chip">
                  <span>DRL Received Date: {formatDateRange(
                    appliedFilters.drlReceivedFrom || '',
                    appliedFilters.drlReceivedTo || ''
                  )}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.drlReceivedFrom;
                      delete newFilters.drlReceivedTo;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, drlReceivedFrom: '', drlReceivedTo: '' }));
                    }}
                    title="Remove DRL received date filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              {(appliedFilters.createdDateFrom || appliedFilters.createdDateTo) && (
                <div className="filter-chip">
                  <span>Created Date: {formatDateRange(
                    appliedFilters.createdDateFrom || '',
                    appliedFilters.createdDateTo || ''
                  )}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.createdDateFrom;
                      delete newFilters.createdDateTo;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, createdDateFrom: '', createdDateTo: '' }));
                    }}
                    title="Remove created date filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              {appliedFilters.practiceCodes && appliedFilters.practiceCodes.length > 0 && appliedFilters.practiceCodes.map((practiceCode) => (
                <div key={practiceCode} className="filter-chip">
                  <span>Practice: {practices.find(p => p.code === practiceCode)?.name || practiceCode}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      if (newFilters.practiceCodes) {
                        newFilters.practiceCodes = newFilters.practiceCodes.filter(code => code !== practiceCode);
                        if (newFilters.practiceCodes.length === 0) {
                          delete newFilters.practiceCodes;
                        }
                      }
                      setFilters(newFilters);
                      handlePracticeRemove(practiceCode);
                    }}
                    title="Remove practice filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              ))}
              {appliedFilters.locationCodes && appliedFilters.locationCodes.length > 0 && appliedFilters.locationCodes.map((locationCode) => (
                <div key={locationCode} className="filter-chip">
                  <span>Location: {locations.find(l => l.code === locationCode)?.name || locationCode}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      if (newFilters.locationCodes) {
                        newFilters.locationCodes = newFilters.locationCodes.filter(code => code !== locationCode);
                        if (newFilters.locationCodes.length === 0) {
                          delete newFilters.locationCodes;
                        }
                      }
                      setFilters(newFilters);
                      setLocalFilters(prev => ({
                        ...prev,
                        locationCodes: prev.locationCodes.filter(code => code !== locationCode)
                      }));
                    }}
                    title="Remove location filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              ))}
              {appliedFilters.drlNumber && (
                <div className="filter-chip">
                  <span>DRL Number: {appliedFilters.drlNumber}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.drlNumber;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, drlNumber: '' }));
                    }}
                    title="Remove DRL number filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              {(appliedFilters.reconciliationDateFrom || appliedFilters.reconciliationDateTo) && (
                <div className="filter-chip">
                  <span>Reconciliation Date: {formatDateRange(
                    appliedFilters.reconciliationDateFrom || '',
                    appliedFilters.reconciliationDateTo || ''
                  )}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.reconciliationDateFrom;
                      delete newFilters.reconciliationDateTo;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, reconciliationDateFrom: '', reconciliationDateTo: '' }));
                    }}
                    title="Remove reconciliation date filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              {(appliedFilters.completedDateFrom || appliedFilters.completedDateTo) && (
                <div className="filter-chip">
                  <span>Completed Date: {formatDateRange(
                    appliedFilters.completedDateFrom || '',
                    appliedFilters.completedDateTo || ''
                  )}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.completedDateFrom;
                      delete newFilters.completedDateTo;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, completedDateFrom: '', completedDateTo: '' }));
                    }}
                    title="Remove completed date filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              {appliedFilters.statuses && appliedFilters.statuses.length > 0 && appliedFilters.statuses.map((status) => {
                const statusLabels = {
                  'NOT_STARTED': 'Not Started',
                  'IN_PROGRESS': 'In Progress',
                  'UNDER_CLARIFICATIONS': 'Under Clarifications',
                  'COMPLETED': 'Completed',
                  'OVER_POSTED': 'Over Posted'
                };
                return (
                  <div key={status} className="filter-chip">
                    <span>Status: {statusLabels[status] || status}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.statuses) {
                          newFilters.statuses = newFilters.statuses.filter(s => s !== status);
                          if (newFilters.statuses.length === 0) {
                            delete newFilters.statuses;
                          }
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({
                          ...prev,
                          statuses: prev.statuses.filter(s => s !== status)
                        }));
                      }}
                      title="Remove status filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.assigneeIds && appliedFilters.assigneeIds.length > 0 && appliedFilters.assigneeIds.map((assigneeId) => {
                const user = users.find(u => (u.userId || u.id) === assigneeId);
                const label = user && user.firstName && user.lastName 
                  ? `${user.firstName} ${user.lastName}` 
                  : user?.email || assigneeId;
                return (
                  <div key={assigneeId} className="filter-chip">
                    <span>Assignee: {label}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.assigneeIds) {
                          newFilters.assigneeIds = newFilters.assigneeIds.filter(id => id !== assigneeId);
                          if (newFilters.assigneeIds.length === 0) {
                            delete newFilters.assigneeIds;
                          }
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({
                          ...prev,
                          assigneeIds: prev.assigneeIds.filter(id => id !== assigneeId)
                        }));
                      }}
                      title="Remove assignee filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.reporteeIds && appliedFilters.reporteeIds.length > 0 && appliedFilters.reporteeIds.map((reporteeId) => {
                const user = users.find(u => (u.userId || u.id) === reporteeId);
                const label = user && user.firstName && user.lastName 
                  ? `${user.firstName} ${user.lastName}` 
                  : user?.email || reporteeId;
                return (
                  <div key={reporteeId} className="filter-chip">
                    <span>Reporter: {label}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.reporteeIds) {
                          newFilters.reporteeIds = newFilters.reporteeIds.filter(id => id !== reporteeId);
                          if (newFilters.reporteeIds.length === 0) {
                            delete newFilters.reporteeIds;
                          }
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({
                          ...prev,
                          reporteeIds: prev.reporteeIds.filter(id => id !== reporteeId)
                        }));
                      }}
                      title="Remove reporter filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.reconciliationStatuses && appliedFilters.reconciliationStatuses.length > 0 && appliedFilters.reconciliationStatuses.map((status) => {
                const statusLabels = {
                  'RECONCILED': 'Reconciled',
                  'OVER_RECONCILED': 'Excess Reconciled',
                  'UNRECONCILED': 'Pending Reconciliation'
                };
                return (
                  <div key={status} className="filter-chip">
                    <span>Reconciliation: {statusLabels[status] || status}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.reconciliationStatuses) {
                          newFilters.reconciliationStatuses = newFilters.reconciliationStatuses.filter(s => s !== status);
                          if (newFilters.reconciliationStatuses.length === 0) {
                            delete newFilters.reconciliationStatuses;
                          }
                        }
                        setFilters(newFilters);
                        handleRemoveFromArray('reconciliationStatuses', status);
                      }}
                      title="Remove reconciliation status filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              <button
                className="clear-all-filters-btn"
                onClick={clearFilters}
                title="Clear all filters"
              >
                Clear All
              </button>
            </div>
          </div>
        );
      })()}

      {/* Primary KPI Cards - Only show if we have amount data */}
      {hasAmountData && (
        <>
          {/* Section 1: Payment Type Breakdown */}
          <div className="kpi-section">
            <div className="section-header">
              <h2 className="section-title">
                Payment Type Breakdown
                <span className="info-icon-tooltip">
                  <CustomTooltip text="eCheck + Paper Check + Credit Card = Total DRL Amount" position="bottom" showArrow={false}>
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{ marginLeft: '8px', color: '#6b7280', cursor: 'pointer' }}
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                  </CustomTooltip>
                </span>
              </h2>
            </div>
            <div className="stats-grid amounts-added-grid">
              <div className="stat-card payment-type-card">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon echeck-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">eCheck Sum</div>
                  <div className="stat-value">{formatCurrency(amountSummary?.eCheckAmount)}</div>
                  <div className="stat-meta">
                    {amountSummary?.totalAmount 
                      ? `${((amountSummary.eCheckAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>

              <div className="stat-card payment-type-card">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon paper-check-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4h16v16H4V4z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M8 12h8M8 8h8M8 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Paper Check</div>
                  <div className="stat-value">{formatCurrency(amountSummary?.paperCheckAmount)}</div>
                  <div className="stat-meta">
                    {amountSummary?.totalAmount 
                      ? `${((amountSummary.paperCheckAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>

              <div className="stat-card payment-type-card">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon credit-card-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M2 10h20" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Credit Card</div>
                  <div className="stat-value">{formatCurrency(amountSummary?.creditCardAmount)}</div>
                  <div className="stat-meta">
                    {amountSummary?.totalAmount 
                      ? `${((amountSummary.creditCardAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>

              <div className="stat-card primary-highlight">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon dollar-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" fill="currentColor"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">= Total DRL Amount</div>
                  <div className="stat-value">{formatCurrency(amountSummary?.totalAmount)}</div>
                  <div className="stat-meta">{totalCount || 0} DRLs</div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Posting Breakdown */}
          <div className="kpi-section">
            <div className="section-header">
              <h2 className="section-title">
                Posting Breakdown
                <span className="info-icon-tooltip">
                  <CustomTooltip text="Onshore Posting + Offshore Posting + Other Amount = Total Posting" position="bottom" showArrow={false}>
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{ marginLeft: '8px', color: '#6b7280', cursor: 'pointer' }}
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                  </CustomTooltip>
                </span>
              </h2>
            </div>
            <div className="stats-grid amounts-posted-grid">
              <div className="stat-card posting-card">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon onshore-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Onshore Posting</div>
                  <div className="stat-value">{formatCurrency(amountSummary?.onshorePostedAmount)}</div>
                  <div className="stat-meta">
                    {amountSummary?.totalAmount 
                      ? `${((amountSummary.onshorePostedAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>

              <div className="stat-card posting-card">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon offshore-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M2 12h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Offshore Posting</div>
                  <div className="stat-value">{formatCurrency(amountSummary?.offshorePostedAmount)}</div>
                  <div className="stat-meta">
                    {amountSummary?.totalAmount 
                      ? `${((amountSummary.offshorePostedAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>

              <div className="stat-card posting-card">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon other-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Other Amount</div>
                  <div className="stat-value">{formatCurrency(amountSummary?.otherAmount)}</div>
                  <div className="stat-meta">
                    {amountSummary?.totalAmount 
                      ? `${((amountSummary.otherAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>

              <div className="stat-card primary-highlight">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon dollar-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" fill="currentColor"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">= Total Posting</div>
                  <div className="stat-value">{formatCurrency(
                    (amountSummary?.onshorePostedAmount || 0) + 
                    (amountSummary?.offshorePostedAmount || 0) + 
                    (amountSummary?.otherAmount || 0)
                  )}</div>
                  <div className="stat-meta">Total posted amounts</div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Bank and Posting Status */}
          <div className="kpi-section">
            <div className="section-header">
              <h2 className="section-title">
                Bank and Posting Status
                <span className="info-icon-tooltip">
                  <CustomTooltip text="Bank reconciliation and remaining amounts" position="bottom" showArrow={false}>
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{ marginLeft: '8px', color: '#6b7280', cursor: 'pointer' }}
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                  </CustomTooltip>
                </span>
              </h2>
            </div>
            <div className="stats-grid amounts-consolidated-grid">
              <div className="stat-card bank-card">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon bank-received-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M5 9h14M5 13h14M5 17h14" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Received in Bank</div>
                  <div className="stat-value">{formatCurrency(
                    (amountSummary?.totalAmount || 0) - (amountSummary?.remainingBankAmount || 0)
                  )}</div>
                  <div className="stat-meta">
                    {amountSummary?.totalAmount 
                      ? `${(((amountSummary.totalAmount - amountSummary.remainingBankAmount) / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                      : '0% of total'}
                  </div>
                </div>
              </div>

              <div className="stat-card bank-card">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon bank-remaining-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M19 21v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 3h18v18H3V3zm16 16V5H5v14h14z" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M7 7h10M7 11h10M7 15h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Remaining in Bank</div>
                  <div className="stat-value">{formatCurrency(amountSummary?.remainingBankAmount)}</div>
                  <div className="stat-meta">
                    {amountSummary?.totalAmount 
                      ? `${((amountSummary.remainingBankAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>

              <div className="stat-card bank-card remaining-card">
                <div className="stat-icon-wrapper">
                  <div className="stat-icon remaining-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Remaining in Posting</div>
                  <div className="stat-value">{formatCurrency(amountSummary?.remainingAmount)}</div>
                  <div className="stat-meta">
                    {amountSummary?.totalAmount 
                      ? `${((amountSummary.remainingAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Show error message if API failed, but don't block dashboard rendering */}
      {error && (
        <div className="dashboard-error-notice" style={{ 
          padding: '16px', 
          marginBottom: '24px', 
          backgroundColor: '#fef3c7', 
          border: '1px solid #f59e0b', 
          borderRadius: '8px',
          color: '#92400e'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>⚠️</span>
            <span style={{ flex: 1 }}>
              <strong>Unable to load dashboard data:</strong> {error}
            </span>
            <button 
              onClick={fetchKPIData} 
              style={{
                padding: '6px 12px',
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Reconciliation Breakdown Section */}
      {kpiData?.single?.reconciliationBreakdown && kpiData.single.reconciliationBreakdown.length > 0 && (
        <div className="kpi-section reconciliation-section">
          <div className="section-header">
            <h2 className="section-title">Reconciliation Status</h2>
          </div>
          <div className="reconciliation-grid">
            <div className="stat-card reconciliation-card unreconciled">
              <div className="stat-icon-wrapper">
                <div className="stat-icon reconciliation-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
              <div className="stat-content">
                <div className="stat-label">Unreconciled</div>
                <div className="stat-value">{kpiData.single.unreconciledCount || 0}</div>
                <div className="stat-meta">
                  {totalCount 
                    ? `${(((kpiData.single.unreconciledCount || 0) / totalCount) * 100).toFixed(1)}% of total`
                    : '0%'}
                </div>
              </div>
            </div>

            <div className="stat-card reconciliation-card reconciled">
              <div className="stat-icon-wrapper">
                <div className="stat-icon reconciliation-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 4L12 14.01l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="stat-content">
                <div className="stat-label">Reconciled</div>
                <div className="stat-value">{kpiData.single.reconciledCount || 0}</div>
                <div className="stat-meta">
                  {totalCount 
                    ? `${(((kpiData.single.reconciledCount || 0) / totalCount) * 100).toFixed(1)}% of total`
                    : '0%'}
                </div>
              </div>
            </div>

            <div className="stat-card reconciliation-card over-reconciled">
              <div className="stat-icon-wrapper">
                <div className="stat-icon reconciliation-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="stat-content">
                <div className="stat-label">Over Reconciled</div>
                <div className="stat-value">{kpiData.single.overReconciledCount || 0}</div>
                <div className="stat-meta">
                  {totalCount 
                    ? `${(((kpiData.single.overReconciledCount || 0) / totalCount) * 100).toFixed(1)}% of total`
                    : '0%'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Breakdown Section - Always show, even if empty */}
      <div className="kpi-section status-section">
        <div className="section-header">
          <h2 className="section-title">Status Breakdown</h2>
        </div>
        {statusBreakdown && statusBreakdown.length > 0 ? (
          <div className="status-grid">
            {statusBreakdown.map((status) => (
              <div key={status.status} className="status-card">
                <div className="status-header">
                  <div 
                    className="status-indicator" 
                    style={{ backgroundColor: STATUS_COLORS[status.status] }}
                  ></div>
                  <span className="status-name">{STATUS_LABELS[status.status] || status.status}</span>
                </div>
                <div className="status-stats">
                  <div className="status-count">{status.count}</div>
                  <div className="status-percentage">{status.percentage.toFixed(1)}%</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ 
            padding: '32px', 
            textAlign: 'center', 
            color: '#6b7280',
            backgroundColor: '#fafbfc',
            borderRadius: '8px',
            border: '1px dashed #d1d5db'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📊</div>
            <p style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: '500', color: '#374151' }}>No status data available</p>
            {error && (
              <p style={{ fontSize: '13px', margin: '0', color: '#9ca3af' }}>
                Status information could not be loaded due to insufficient permissions or API error.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Charts Section - Only show if we have status data */}
      {statusBreakdown && statusBreakdown.length > 0 && (
        <div className="charts-grid">
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Status Distribution</h3>
              <div className="chart-subtitle">DRL count by status</div>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={prepareStatusChartData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {prepareStatusChartData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || '#8884d8'} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} DRLs (${props.payload.percentage.toFixed(1)}%)`,
                    name
                  ]}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="chart-legend">
              {prepareStatusChartData().map((entry, index) => (
                <div key={index} className="legend-item">
                  <div 
                    className="legend-color" 
                    style={{ backgroundColor: STATUS_COLORS[entry.status] || '#8884d8' }}
                  ></div>
                  <span className="legend-label">{entry.name}</span>
                  <span className="legend-percentage">{entry.percentage.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Reconciliation Status Breakdown</h3>
              <div className="chart-subtitle">Count and percentage by reconciliation status</div>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={prepareReconciliationStatusChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  stroke="#6b7280"
                  tick={{ fontSize: 12 }}
                />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} DRLs (${props.payload.percentage.toFixed(1)}%)`,
                    'Count'
                  ]}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                />
                <Bar 
                  dataKey="count" 
                  radius={[8, 8, 0, 0]}
                >
                  {prepareReconciliationStatusChartData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="chart-legend">
              {prepareReconciliationStatusChartData().map((entry, index) => (
                <div key={index} className="legend-item">
                  <div 
                    className="legend-color" 
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className="legend-label">{entry.name}</span>
                  <span className="legend-percentage">{entry.percentage.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filter Sidebar */}
      {showFilterSidebar && (
        <>
          <div className="filter-sidebar-overlay" onClick={() => setShowFilterSidebar(false)}></div>
          <div className="filter-sidebar">
            <div className="filter-sidebar-header">
              <h2>Custom Filters</h2>
              <button 
                className="filter-sidebar-close"
                onClick={() => setShowFilterSidebar(false)}
                title="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="filter-sidebar-content">
              {/* Status Filter */}
              <div className="sidebar-filter-group">
                <label>Status</label>
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={[
                        { value: 'NOT_STARTED', label: 'Not Started' },
                        { value: 'IN_PROGRESS', label: 'In Progress' },
                        { value: 'UNDER_CLARIFICATIONS', label: 'Under Clarifications' },
                        { value: 'COMPLETED', label: 'Completed' },
                        { value: 'OVER_POSTED', label: 'Over Posted' }
                      ].filter(opt => !localFilters.statuses || !localFilters.statuses.includes(opt.value))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddToArray('statuses', value);
                      }}
                      placeholder="Select Status"
                      maxVisibleItems={5}
                    />
                  </div>
                  {localFilters.statuses && localFilters.statuses.length > 0 && (
                    <div className="selected-practices">
                      {localFilters.statuses.map((status) => {
                        const statusLabels = {
                          'NOT_STARTED': 'Not Started',
                          'IN_PROGRESS': 'In Progress',
                          'UNDER_CLARIFICATIONS': 'Under Clarifications',
                          'COMPLETED': 'Completed',
                          'OVER_POSTED': 'Over Posted'
                        };
                        return (
                          <div key={status} className="selected-practice-chip">
                            <span>{statusLabels[status] || status}</span>
                            <button
                              onClick={() => handleRemoveFromArray('statuses', status)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Practice Filter */}
              <div className="sidebar-filter-group">
                <label>Practice</label>
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={practices
                        .filter(p => !localFilters.practiceCodes?.includes(p.code))
                        .map(p => ({ value: p.code, label: `${p.code} - ${p.name}` }))}
                      value={selectedPracticeValue}
                      onChange={handlePracticeAdd}
                      placeholder="Select Practice"
                      maxVisibleItems={5}
                    />
                  </div>
                  {localFilters.practiceCodes && localFilters.practiceCodes.length > 0 && (
                    <div className="selected-practices">
                      {localFilters.practiceCodes.map((practiceCode) => (
                        <div key={practiceCode} className="selected-practice-chip">
                          <span>{practices.find(p => p.code === practiceCode)?.name || practiceCode}</span>
                          <button
                            onClick={() => handlePracticeRemove(practiceCode)}
                            className="chip-remove-btn"
                          >
                            <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Location Filter */}
              <div className="sidebar-filter-group">
                <label>Location</label>
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={locations
                        .filter(l => l.isActive !== false)
                        .filter(l => !localFilters.locationCodes || !localFilters.locationCodes.includes(l.code))
                        .map(location => ({
                          value: location.code,
                          label: location.name || location.code
                        }))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddToArray('locationCodes', value);
                      }}
                      placeholder="Select Location"
                      disabled={!localFilters.practiceCodes || localFilters.practiceCodes.length === 0}
                      maxVisibleItems={5}
                    />
                  </div>
                  {localFilters.locationCodes && localFilters.locationCodes.length > 0 && (
                    <div className="selected-practices">
                      {localFilters.locationCodes.map((locationCode) => {
                        const location = locations.find(l => l.code === locationCode);
                        const label = location?.name || locationCode;
                        return (
                          <div key={locationCode} className="selected-practice-chip">
                            <span>{label}</span>
                            <button
                              onClick={() => handleRemoveFromArray('locationCodes', locationCode)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Assignee Filter */}
              <div className="sidebar-filter-group">
                <label>Assignee Name</label>
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={users
                        .filter(user => !localFilters.assigneeIds || !localFilters.assigneeIds.includes(user.userId || user.id))
                        .map(user => ({
                          value: user.userId || user.id,
                          label: user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}` 
                            : user.email || 'Unknown User'
                        }))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddToArray('assigneeIds', value);
                      }}
                      placeholder="Select Assignee"
                      maxVisibleItems={5}
                    />
                  </div>
                  {localFilters.assigneeIds && localFilters.assigneeIds.length > 0 && (
                    <div className="selected-practices">
                      {localFilters.assigneeIds.map((assigneeId) => {
                        const user = users.find(u => (u.userId || u.id) === assigneeId);
                        const label = user && user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : user?.email || assigneeId;
                        return (
                          <div key={assigneeId} className="selected-practice-chip">
                            <span>{label}</span>
                            <button
                              onClick={() => handleRemoveFromArray('assigneeIds', assigneeId)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Reporter Filter */}
              <div className="sidebar-filter-group">
                <label>Reporter Name</label>
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={users
                        .filter(user => !localFilters.reporteeIds || !localFilters.reporteeIds.includes(user.userId || user.id))
                        .map(user => ({
                          value: user.userId || user.id,
                          label: user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}` 
                            : user.email || 'Unknown User'
                        }))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddToArray('reporteeIds', value);
                      }}
                      placeholder="Select Reporter"
                      maxVisibleItems={5}
                    />
                  </div>
                  {localFilters.reporteeIds && localFilters.reporteeIds.length > 0 && (
                    <div className="selected-practices">
                      {localFilters.reporteeIds.map((reporteeId) => {
                        const user = users.find(u => (u.userId || u.id) === reporteeId);
                        const label = user && user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : user?.email || reporteeId;
                        return (
                          <div key={reporteeId} className="selected-practice-chip">
                            <span>{label}</span>
                            <button
                              onClick={() => handleRemoveFromArray('reporteeIds', reporteeId)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* DRL Number Filter */}
              <div className="sidebar-filter-group">
                <label>DRL Number</label>
                <input
                  type="text"
                  value={localFilters.drlNumber || ''}
                  onChange={(e) => handleFilterChange('drlNumber', e.target.value)}
                  placeholder="Enter DRL number (e.g., DRL* or DRL)"
                  className="sidebar-filter-input"
                />
              </div>

              {/* Reconciliation Status Filter */}
              <div className="sidebar-filter-group">
                <label>Reconciliation Status</label>
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={[
                        { value: 'RECONCILED', label: 'Reconciled' },
                        { value: 'OVER_RECONCILED', label: 'Excess Reconciled' },
                        { value: 'UNRECONCILED', label: 'Pending Reconciliation' }
                      ].filter(opt => !localFilters.reconciliationStatuses || !localFilters.reconciliationStatuses.includes(opt.value))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddToArray('reconciliationStatuses', value);
                      }}
                      placeholder="Select Reconciliation Status"
                      maxVisibleItems={5}
                    />
                  </div>
                  {localFilters.reconciliationStatuses && localFilters.reconciliationStatuses.length > 0 && (
                    <div className="selected-practices">
                      {localFilters.reconciliationStatuses.map((status) => {
                        const statusLabels = {
                          'RECONCILED': 'Reconciled',
                          'OVER_RECONCILED': 'Excess Reconciled',
                          'UNRECONCILED': 'Pending Reconciliation'
                        };
                        return (
                          <div key={status} className="selected-practice-chip">
                            <span>{statusLabels[status] || status}</span>
                            <button
                              onClick={() => handleRemoveFromArray('reconciliationStatuses', status)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Date Filters Section */}
              <div className="sidebar-filter-group">
                <label>DRL Received Date From</label>
                <USDateInput
                  name="drlReceivedFrom"
                  value={localFilters.drlReceivedFrom || ''}
                  onChange={(e) => handleFilterChange('drlReceivedFrom', e.target.value)}
                  className="sidebar-filter-input"
                />
              </div>
              
              <div className="sidebar-filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  DRL Received Date To{localFilters.drlReceivedFrom ? <span className="required">*</span> : ''}
                  <CustomTooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </CustomTooltip>
                </label>
                <USDateInput
                  ref={drlReceivedToRef}
                  name="drlReceivedTo"
                  value={localFilters.drlReceivedTo || ''}
                  onChange={(e) => handleFilterChange('drlReceivedTo', e.target.value)}
                  min={localFilters.drlReceivedFrom || undefined}
                  className="sidebar-filter-input"
                  disabled={!localFilters.drlReceivedFrom || localFilters.drlReceivedFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for DRL Received Date.')}
                />
              </div>

              <div className="sidebar-filter-group">
                <label>Created Date From</label>
                <USDateInput
                  name="createdDateFrom"
                  value={localFilters.createdDateFrom || ''}
                  onChange={(e) => handleFilterChange('createdDateFrom', e.target.value)}
                  className="sidebar-filter-input"
                />
              </div>
              
              <div className="sidebar-filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Created Date To{localFilters.createdDateFrom ? <span className="required">*</span> : ''}
                  <CustomTooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </CustomTooltip>
                </label>
                <USDateInput
                  ref={createdDateToRef}
                  name="createdDateTo"
                  value={localFilters.createdDateTo || ''}
                  onChange={(e) => handleFilterChange('createdDateTo', e.target.value)}
                  min={localFilters.createdDateFrom || undefined}
                  className="sidebar-filter-input"
                  disabled={!localFilters.createdDateFrom || localFilters.createdDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Created Date.')}
                />
              </div>

              <div className="sidebar-filter-group">
                <label>Posting Completed From</label>
                <USDateInput
                  name="completedDateFrom"
                  value={localFilters.completedDateFrom || ''}
                  onChange={(e) => handleFilterChange('completedDateFrom', e.target.value)}
                  className="sidebar-filter-input"
                />
              </div>
              
              <div className="sidebar-filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Posting Completed To{localFilters.completedDateFrom ? <span className="required">*</span> : ''}
                  <CustomTooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </CustomTooltip>
                </label>
                <USDateInput
                  ref={completedDateToRef}
                  name="completedDateTo"
                  value={localFilters.completedDateTo || ''}
                  onChange={(e) => handleFilterChange('completedDateTo', e.target.value)}
                  min={localFilters.completedDateFrom || undefined}
                  className="sidebar-filter-input"
                  disabled={!localFilters.completedDateFrom || localFilters.completedDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Completed Date.')}
                />
              </div>

              <div className="sidebar-filter-group">
                <label>Reconciliation From</label>
                <USDateInput
                  name="reconciliationDateFrom"
                  value={localFilters.reconciliationDateFrom || ''}
                  onChange={(e) => handleFilterChange('reconciliationDateFrom', e.target.value)}
                  className="sidebar-filter-input"
                />
              </div>
              
              <div className="sidebar-filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Reconciliation To{localFilters.reconciliationDateFrom ? <span className="required">*</span> : ''}
                  <CustomTooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </CustomTooltip>
                </label>
                <USDateInput
                  ref={reconciliationDateToRef}
                  name="reconciliationDateTo"
                  value={localFilters.reconciliationDateTo || ''}
                  onChange={(e) => handleFilterChange('reconciliationDateTo', e.target.value)}
                  min={localFilters.reconciliationDateFrom || undefined}
                  className="sidebar-filter-input"
                  disabled={!localFilters.reconciliationDateFrom || localFilters.reconciliationDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Reconciliation Date.')}
                />
              </div>
            </div>
            <div className="filter-sidebar-footer">
              <button onClick={() => {
                clearFilters();
                setShowFilterSidebar(false);
              }} className="sidebar-clear-button">
                Clear All
              </button>
              <button onClick={() => {
                const success = applyFilters();
                if (success) {
                  setShowFilterSidebar(false);
                }
              }} className="sidebar-apply-button">
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DrlDashboard;
